/*
 * Replacement for @coreui/vue-chartjs v1 (Vue 2 + Chart.js 2) on top of Chart.js 4.
 * Same component names/props (datasets, labels, options, plugins). Chart.js 2 style options
 * (xAxes/yAxes, legend, tooltips, gridLines) are translated to the Chart.js 4 shape.
 */
import { h, markRaw } from 'vue'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function convertAxis (axis) {
  if (!axis) return axis
  const out = Object.assign({}, axis)
  if (out.gridLines) {
    out.grid = Object.assign({}, out.gridLines)
    if (out.grid.zeroLineColor) delete out.grid.zeroLineColor
    delete out.gridLines
  }
  if (out.ticks) {
    const t = Object.assign({}, out.ticks)
    if (t.min !== undefined) { out.min = t.min; delete t.min }
    if (t.max !== undefined) { out.max = t.max; delete t.max }
    if (t.beginAtZero !== undefined) { out.beginAtZero = t.beginAtZero; delete t.beginAtZero }
    if (t.fontColor) { t.color = t.fontColor; delete t.fontColor }
    if (t.fontSize) { t.font = { size: t.fontSize }; delete t.fontSize }
    out.ticks = t
  }
  delete out.id
  return out
}

/** Chart.js 2 -> 4 option shape (only the parts the panel used) */
export function convertOptions (options) {
  if (!options) return {}
  const out = Object.assign({}, options)
  const plugins = Object.assign({}, out.plugins || {})
  if (out.legend !== undefined) { plugins.legend = out.legend; delete out.legend }
  if (out.tooltips !== undefined) { plugins.tooltip = out.tooltips; delete out.tooltips }
  if (out.title !== undefined && typeof out.title === 'object' && !plugins.title) { plugins.title = out.title; delete out.title }
  out.plugins = plugins
  if (out.scales && (out.scales.xAxes || out.scales.yAxes)) {
    const scales = {}
    ;(out.scales.xAxes || []).forEach((a, i) => { scales[i ? `x${i}` : 'x'] = convertAxis(a) })
    ;(out.scales.yAxes || []).forEach((a, i) => { scales[i ? `y${i}` : 'y'] = convertAxis(a) })
    out.scales = scales
  }
  return out
}

function chartFactory (name, type) {
  return {
    name,
    props: {
      datasets: Array,
      labels: [Array, String],
      options: Object,
      plugins: Array
    },
    computed: {
      computedLabels () {
        if (this.labels && typeof this.labels !== 'string') return this.labels
        if (!this.datasets || !this.datasets[0] || !this.datasets[0].data) return []
        const empty = Array(this.datasets[0].data.length).fill('')
        if (this.labels === 'indexes') return empty.map((u, i) => i + 1)
        if (this.labels === 'months') return empty.map((u, i) => MONTHS[i % 12])
        return empty
      },
      chartConfig () {
        return {
          type,
          data: { datasets: (this.datasets || []).map((d) => Object.assign({}, d)), labels: this.computedLabels },
          options: convertOptions(this.options),
          plugins: this.plugins
        }
      }
    },
    watch: {
      chartConfig () {
        if (!this.chart) return
        const cfg = this.chartConfig
        this.chart.data = cfg.data
        this.chart.options = cfg.options
        this.chart.update()
      }
    },
    mounted () {
      this.chart = markRaw(new Chart(this.$refs.canvas.getContext('2d'), this.chartConfig))
    },
    beforeUnmount () {
      if (this.chart) this.chart.destroy()
      this.chart = null
    },
    render () {
      return h('div', [h('canvas', { ref: 'canvas' })])
    }
  }
}

export const CChartBar = chartFactory('CChartBar', 'bar')
export const CChartLine = chartFactory('CChartLine', 'line')
export const CChartDoughnut = chartFactory('CChartDoughnut', 'doughnut')
export const CChartRadar = chartFactory('CChartRadar', 'radar')
export const CChartPie = chartFactory('CChartPie', 'pie')
export const CChartPolarArea = chartFactory('CChartPolarArea', 'polarArea')
